import type { ReactNode } from 'react';
import { Save, Undo2, Maximize2, Minimize2, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useTranslation } from '@/hooks/useTranslation';
import type { TabularFormat } from '@/lib/export/tabularExport';

/** The table-download control — supplied by `EditableTable` (`download` prop). */
export interface ToolbarDownload {
  disabled: boolean;
  onDownload: (format: TabularFormat) => void;
}

export interface ToolbarProps {
  dirtyCount: number;
  /**
   * Number of dirty rows blocked from save by validation errors. When > 0
   * the Save button is disabled and a tooltip explains why. The host
   * `<TooltipProvider>` is supplied by the app shell; consumers mounting
   * `EditableTable` outside that provider must wrap themselves.
   */
  errorRowCount?: number;
  isSaving: boolean;
  isFullscreen: boolean;
  onSave?: () => void;
  onDiscard?: () => void;
  onToggleFullscreen: () => void;
  extra?: ReactNode;
  center?: ReactNode;
  right?: ReactNode;
  title?: ReactNode;
  /**
   * The Columns-panel trigger (`ColumnManagerPanel`), rendered beside the
   * fullscreen button. Supplied by `EditableTable` when column management
   * is enabled and a toolbar is visible — the standard placement.
   */
  columnsPanel?: ReactNode;
  /**
   * The Download control (icon → CSV / Excel menu), rendered first in the
   * table-tools cluster, before the Columns trigger and fullscreen.
   */
  download?: ToolbarDownload;
}

export function Toolbar({
  dirtyCount,
  errorRowCount = 0,
  isSaving,
  isFullscreen,
  onSave,
  onDiscard,
  onToggleFullscreen,
  extra,
  center,
  right,
  title,
  columnsPanel,
  download,
}: ToolbarProps) {
  const { t } = useTranslation('spreadsheet');
  const hasErrors = errorRowCount > 0;
  const saveDisabled = dirtyCount === 0 || isSaving || hasErrors;

  let saveLabel: string;
  if (isSaving) {
    saveLabel = t('toolbar.saving');
  } else {
    const parts = [t('toolbar.save')];
    if (dirtyCount > 0) parts.push(`(${dirtyCount})`);
    if (hasErrors) parts.push(`· ${t('toolbar.errorCount', { count: errorRowCount })}`);
    saveLabel = parts.join(' ');
  }

  const saveButton = (
    <Button
      variant="default"
      size="sm"
      onClick={onSave}
      disabled={saveDisabled}
      className="gap-1.5 bg-success hover:bg-success/80 text-success-foreground"
    >
      <Save className="w-4 h-4" />
      {saveLabel}
    </Button>
  );

  return (
    // stopPropagation on keyDown isolates toolbar inputs (search, future
    // filter UIs) from the EditableTable container's global key handler
    // — otherwise Ctrl+A / Backspace / arrows reach the cell-nav handler
    // instead of the input the user is typing in.
    <div
      data-toolbar-root=""
      onKeyDown={(e) => e.stopPropagation()}
      className={`editable-table-toolbar relative flex items-center justify-between gap-3 px-3 py-2 border-b border-border ${isFullscreen ? 'bg-muted' : 'bg-muted/50'}`}>
      {/* Left slot (actions / Save+Discard) renders only when it has content —
          an empty div would still occupy a flex position and, with the
          parent's gap-3, push the search off the margin on action-less
          read-only grids. */}
      {(extra || onSave) && (
      <div className="flex items-center gap-2 sm:min-w-0">
        {extra}
        {onSave && (
          <>
            {hasErrors ? (
              // Local TooltipProvider so the error tooltip works regardless
              // of whether the host page wraps the route tree in one.
              // <span> wrap: Radix Tooltip needs a non-disabled trigger to
              // capture hover; a disabled <Button> sets pointer-events: none.
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0}>{saveButton}</span>
                  </TooltipTrigger>
                  <TooltipContent>{t('toolbar.errorTooltip', { count: errorRowCount })}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : saveButton}
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={onDiscard}
              disabled={dirtyCount === 0 || isSaving}
            >
              <Undo2 className="w-4 h-4" />
              {t('toolbar.discardChanges')}
            </Button>
          </>
        )}
      </div>
      )}
      <div className="absolute left-1/2 -translate-x-1/2 max-w-[60%] overflow-hidden">
        {isFullscreen && title && !center && (
          <h2 className="text-sm font-medium flex items-center gap-2 whitespace-nowrap">
            <span className="sr-only">{t('toolbar.currentView')}: </span>
            {title}
          </h2>
        )}
        {center}
      </div>
      {right && (
        // data-toolbar-search-slot: filter chips borrow this slot's width
        // beyond its min-width (useChipOverflow).
        <div data-toolbar-search-slot="" className="flex-1 flex items-center justify-end min-w-0 sm:min-w-[12rem]">
          {right}
        </div>
      )}
      {/* ml-auto: stays right-aligned even when it is the only flex item (no
          left slot, no search — the centered slot is absolutely positioned). */}
      <div className="ml-auto flex items-center gap-1">
        {download && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={download.disabled}
                aria-label={t('toolbar.download')}
                title={t('toolbar.download')}
              >
                <Download className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => download.onDownload('csv')}>{t('toolbar.downloadCsv')}</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => download.onDownload('xlsx')}>{t('toolbar.downloadXlsx')}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {columnsPanel}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onToggleFullscreen}
          aria-label={isFullscreen ? t('toolbar.exitFullscreen') : t('toolbar.enterFullscreen')}
        >
          {isFullscreen ? (
            <Minimize2 className="w-4 h-4" />
          ) : (
            <Maximize2 className="w-4 h-4" />
          )}
        </Button>
      </div>
    </div>
  );
}
